const effectsStore = {
  init: () => {
    const elements = document.querySelectorAll('[data-hover-vertical-slide]');
    elements.forEach(element => {
      element.classList.add('hover-vertical-slide');
      const content = element.textContent.trim();
      element.innerHTML = `<span>${content}</span><span aria-hidden="true">${content}</span>`;
    });
  },

  heroBackground(background: HTMLElement) {
    const columns = background.querySelectorAll('div');
    const positionFollow = {
      current: 50,
      target: 50,
      lastTarget: 50,
      velocity: 0,
      index: 0,
      byIndex: Array.from(columns, () => 50),
      rafId: 0 as number | 0,
    };

    columns.forEach((column, i) => {
      if (i === 2) {
        column.style.opacity = '0.2';
      } else if (i === 1 || i === 3) {
        column.style.opacity = '0.14';
      } else if (i === 0 || i === 4) {
        column.style.opacity = '0.07';
      } else {
        column.style.opacity = '0';
      }
    });

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

    const applyPosition = (clientX: number, clientY: number) => {
      const rect = background.getBoundingClientRect();
      const localX = clientX - rect.left;
      const localY = clientY - rect.top;

      if (localY < 0 || localY > rect.height) {
        return;
      }

      const currentIndex = clamp(Math.round(localX / (rect.width / 8)), 0, columns.length - 1);
      const nextTarget = clamp((localY * 100) / rect.height, 15, 85);

      positionFollow.velocity = nextTarget - positionFollow.lastTarget;
      positionFollow.lastTarget = nextTarget;
      positionFollow.target = nextTarget;
      positionFollow.index = currentIndex;

      if (!positionFollow.rafId) {
        positionFollow.rafId = window.requestAnimationFrame(function tick() {
          const delta = positionFollow.target - positionFollow.current;
          positionFollow.current += delta * 0.06;

          columns.forEach((column, index) => {
            const distance = Math.abs(index - positionFollow.index);
            const maxShift = 6;
            const baseShift = Math.max(-maxShift, Math.min(maxShift, positionFollow.velocity * 0.35));
            const rippleShift = baseShift * Math.pow(0.65, distance);

            const easedFactor = 0.06 * (distance === 0 ? 1 : Math.pow(0.5, distance));
            const target = clamp(positionFollow.target + rippleShift, 15, 85);
            positionFollow.byIndex[index] += (target - positionFollow.byIndex[index]) * easedFactor;

            (column as HTMLElement).style.setProperty(
              '--tw-gradient-via-position',
              `${positionFollow.byIndex[index]}%`,
            );
          });

          if (Math.abs(delta) < 0.1) {
            positionFollow.current = positionFollow.target;
            columns.forEach((column, index) => {
              const distance = Math.abs(index - positionFollow.index);
              const maxShift = 6;
              const baseShift = Math.max(-maxShift, Math.min(maxShift, positionFollow.velocity * 0.35));
              const rippleShift = baseShift * Math.pow(0.65, distance);
              const target = clamp(positionFollow.target + rippleShift, 15, 85);
              positionFollow.byIndex[index] = target;
              (column as HTMLElement).style.setProperty(
                '--tw-gradient-via-position',
                `${positionFollow.byIndex[index]}%`,
              );
            });
            positionFollow.rafId = 0;
            return;
          }

          positionFollow.rafId = window.requestAnimationFrame(tick);
        });
      }

      columns.forEach((column, index) => {
        const distance = Math.abs(index - currentIndex);
        const delayMs = Math.min(distance, 6) * 70;

        column.style.transitionProperty = 'opacity';
        column.style.transitionTimingFunction = 'ease-in-out';
        column.style.transitionDelay = `${delayMs}ms`;

        if (distance === 0) {
          column.style.opacity = '0.2';
          column.style.transitionDuration = '500ms';
        } else if (distance === 1) {
          column.style.opacity = '0.14';
          column.style.transitionDuration = '650ms';
        } else if (distance === 2) {
          column.style.opacity = '0.07';
          column.style.transitionDuration = '800ms';
        } else {
          column.style.opacity = '0';
          column.style.transitionDuration = '1200ms';
        }
      });
    };

    if (window.innerWidth < 1024) {
      // Emulated motion: top-left → bottom-right → bottom-left → top-left
      const points = [
        { x: 0, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
        { x: 0, y: 0 },
      ];

      const legDurationMs = 3000;
      const totalDurationMs = legDurationMs * (points.length - 1);
      const startTime = performance.now();

      const step = (now: number) => {
        const elapsed = (now - startTime) % totalDurationMs;
        const leg = Math.min(Math.floor(elapsed / legDurationMs), points.length - 2);
        const t = (elapsed - leg * legDurationMs) / legDurationMs;

        const from = points[leg];
        const to = points[leg + 1];

        const nx = from.x + (to.x - from.x) * t;
        const ny = from.y + (to.y - from.y) * t;

        const rect = background.getBoundingClientRect();
        applyPosition(rect.left + nx * rect.width, rect.top + ny * rect.height);

        window.requestAnimationFrame(step);
      };

      window.requestAnimationFrame(step);
    } else {
      // Desktop only: react to real mouse input
      window.addEventListener('mousemove', (event: MouseEvent) => {
        applyPosition(event.clientX, event.clientY);
      });
    }
  },

  heroWords(root: HTMLElement) {
    const track = root.firstElementChild as HTMLElement | null;
    if (!track) return;

    const items = Array.from(track.children) as HTMLElement[];
    if (items.length <= 1) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const clone = items[0].cloneNode(true) as HTMLElement;
    clone.setAttribute('aria-hidden', 'true');
    track.appendChild(clone);

    let itemHeight = items[0].getBoundingClientRect().height;
    root.style.height = `${itemHeight}px`;

    const intervalMs = 2000;
    const transitionMs = 600;
    let index = 0;

    track.style.transitionProperty = 'transform';
    track.style.transitionTimingFunction = 'ease-in-out';
    track.style.transitionDuration = `${transitionMs}ms`;

    window.setInterval(() => {
      index += 1;
      track.style.transform = `translateY(-${index * itemHeight}px)`;

      if (index === items.length) {
        window.setTimeout(() => {
          track.style.transitionDuration = '0ms';
          track.style.transform = 'translateY(0px)';
          index = 0;

          void track.offsetHeight;
          track.style.transitionDuration = `${transitionMs}ms`;
        }, transitionMs);
      }
    }, intervalMs);

    // Re-measure on resize
    let resizeTimeout: number | undefined;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        itemHeight = items[0].getBoundingClientRect().height;
        root.style.height = `${itemHeight}px`;

        track.style.transitionDuration = '0ms';
        track.style.transform = `translateY(-${index * itemHeight}px)`;

        void track.offsetHeight;
        track.style.transitionDuration = `${transitionMs}ms`;
      }, 150);
    });
  },

  footerPixels() {
    const wrapper = document.querySelector('[data-footer-pixels]') as HTMLElement | null;
    if (!wrapper) return;

    // Only emulate "hover" when the device doesn't support hover.
    if (window.matchMedia?.('(hover: hover)').matches) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const pixels = Array.from(wrapper.querySelectorAll('[data-footer-pixel]')) as HTMLElement[];
    if (pixels.length === 0) return;

    const gridSize = 10;
    const width = gridSize;
    const height = gridSize * 1.5;

    const activeClass = 'opacity-80';
    const trailLength = 18;
    const tickMs = 110;

    const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
    const idx = (x: number, y: number) => clamp(y, 0, height - 1) * width + clamp(x, 0, width - 1);

    const turnLeft = (dx: number, dy: number) => ({ dx: -dy, dy: dx });
    const turnRight = (dx: number, dy: number) => ({ dx: dy, dy: -dx });

    let x = Math.floor(Math.random() * width);
    let y = Math.floor(Math.random() * height);
    let dx = 1;
    let dy = 0;

    // Trail of indices (newest last)
    const trail: number[] = [];

    const light = (index: number) => pixels[index]?.classList.add(activeClass);
    const dim = (index: number) => pixels[index]?.classList.remove(activeClass);

    const step = () => {
      console.log('step');
      // Occasionally turn to make a "path" feel.
      const r = Math.random();
      if (r < 0.18) {
        ({ dx, dy } = turnLeft(dx, dy));
      } else if (r < 0.36) {
        ({ dx, dy } = turnRight(dx, dy));
      }

      x = (x + dx + width) % width;
      y = (y + dy + height) % height;

      // Sometimes "jump" a little to avoid perfect loops.
      if (Math.random() < 0.08) {
        x = (x + (Math.random() < 0.5 ? -2 : 2) + width) % width;
        y = (y + (Math.random() < 0.5 ? -1 : 1) + height) % height;
      }

      const index = idx(x, y);
      trail.push(index);
      light(index);

      while (trail.length > trailLength) {
        const removed = trail.shift();
        if (typeof removed === 'number') dim(removed);
      }
    };

    let intervalId: number | null = null;
    const start = () => {
      if (intervalId !== null) return;
      // Seed a few pixels so it doesn't start "empty".
      for (let i = 0; i < Math.min(trailLength, 6); i++) step();
      intervalId = window.setInterval(step, tickMs);
    };
    const stop = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
      trail.splice(0).forEach(dim);
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) start();
          else stop();
        }
      }, { root: null, threshold: 0.05 });
      io.observe(wrapper);
    } else {
      start();
    }
  },
};

export default effectsStore;
